import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEnrichmentState } from '../../hooks/useEnrichmentState';
import { userAppContext } from '../../context/Context';

import Alert from '../../global/Alert/Alert';
import Loader from '../../components/Common/Loader';
import EnrichmentModal from './components/EnrichmentModal';
import ValidationMessage from './components/ValidationMessage';
import SharedModal from '../../components/Modal/SharedModal';
import { getItemCount } from '../../components/AdvancedSearch/AdvancedSearchHelpers';
import { UserContext } from '../../types';
import { advancedExamples } from '../../components/AdvancedSearch/AdvancedExamples';
import useFetchSuggestions from '../../hooks/useFetchSuggestions';
import EnrichmentSwarmPlot from './components/EnrichmentGraph';
import { tabs } from './helpers';
import { useEnrichmentValidation, useEnrichmentAPI, useEnrichmentDownload } from './hooks';
import { EnrichmentResult } from './types';
import SharedButton from '@/global/Button/Button';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import SharedBadge from '@/global/Badge/Badge';
import HomeStats from '@/components/Home/HomeStats';
import TabSelector from '@/global/TabSelector/TabSelector';
import BackgroundPages from '@/global/Background/BackgroundPages';
import RestrictedAccess from '@/components/Modal/RestrictedAccess';

const Enrichment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { enrichmentFrom, clearEnrichmentFrom } = useEnrichmentState();
  const { user, authorization, isAuthenticated } = userAppContext() as UserContext;

  const enrichmentIcon = '/static/images/icons/iconsPink/enrichment-icon.svg';

  const initialData = location.state || {};

  const [searchType, setSearchType] = useState(initialData.searchType || 'GENES');
  const [text, setText] = useState(initialData.text || '');
  const [geneFilterType, setGeneFilterType] = useState(
    initialData.selectedFilter || initialData.geneFilterType || 'geneSymbol',
  );

  const [showModal, setShowModal] = useState(false);
  const [showWarningAlert, setShowWarningAlert] = useState(false);
  const [trialLimitDismissed, setTrialLimitDismissed] = useState(false);

  const searchTypeRef = useRef(searchType);

  const userGroups = user?.['cognito:groups'];
  const isAcademic = Array.isArray(userGroups) ? userGroups.includes('ROLE_ACADEMIC') : false;
  const isTrial = user?.['cognito:groups']?.[0] === 'ROLE_TRIAL';

  const [enrichmentParams, setEnrichmentParams] = useState({
    commonVariants: 2,
    commonGenes: 2,
    maxPvalue: 0.05,
    maxScore: 1,
    minScore: 0,
    pageNumber: 0,
    source: isAcademic ? 'CURATED' : 'ALL',
  });

  // Custom hooks
  const { validation, showValidation, validateInput, clearValidation } = useEnrichmentValidation({
    text,
    searchType,
    geneFilterType,
  });

  const {
    enrichmentLoading,
    enrichmentResults,
    showErrorAlert,
    showSuccessAlert,
    showApiKeyAlert,
    showTrialLimitModal,
    performEnrichment,
    clearResults,
    setShowApiKeyAlert,
    setShowTrialLimitModal,
  } = useEnrichmentAPI({
    authorization: authorization as string,
    isAuthenticated,
    enrichmentParams,
  });

  const {
    csvData,
    setCsvData,
    metadataContent,
    setMetadataContent,
    swarmRef,
    downloadZIP,
    downloadGraphPNG,
  } = useEnrichmentDownload();

  const { notFoundItems, fetchSuggestions } = useFetchSuggestions(
    searchType,
    searchTypeRef.current === 'GENES' ? geneFilterType : 'strID',
    authorization,
    isAuthenticated,
  );

  useEffect(() => {
    if (initialData.selectedFilter) {
      setGeneFilterType(initialData.selectedFilter);
    }
  }, [initialData.selectedFilter]);

  useEffect(() => {
    searchTypeRef.current = searchType;
    if (
      searchTypeRef.current === 'GENES' &&
      !(enrichmentFrom?.from === 'advancedSearchSelectedItems' && enrichmentFrom?.selectedFilter === 'name')
    ) {
      setGeneFilterType('geneSymbol');
    }
  }, [searchType, enrichmentFrom]);

  useEffect(() => {
    if (enrichmentResults && swarmRef.current) {
      setTimeout(() => {
        swarmRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [enrichmentResults]);

  useEffect(() => {
    if (!isTrial) return;
    const inputs = text
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line);

    if (inputs.length > 10 && !showTrialLimitModal && !trialLimitDismissed) {
      setShowTrialLimitModal(true);
    } else if (inputs.length <= 10) {
      setShowTrialLimitModal(false);
      setTrialLimitDismissed(false);
    }
  }, [text, isTrial, showTrialLimitModal, trialLimitDismissed]);

  const handleQueryChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const lines = value.split('\n');

    if (lines.length > 4000) {
      const limitedValue = lines.slice(0, 4000).join('\n');
      setText(limitedValue);
    } else {
      setText(value);
    }
  };

  const clearInputText = () => {
    setText('');
    clearValidation();
    clearResults();
  };

  const showExamples = () => {
    const formatFilterType =
      searchTypeRef.current === 'GENES'
        ? geneFilterType.replace('Id', 'ID').replace('geneSymbol', 'symbol')
        : 'strID';

    const exampleItems = advancedExamples[searchTypeRef.current]?.[formatFilterType] || [];
    setText(exampleItems.join('\n'));
  };

  const handleGeneFilterTypeChange = (newFilterType: string) => {
    setGeneFilterType(newFilterType);
  };

  const handleAutoDetectSuggestion = () => {
    if (validation?.detectedType && searchType === 'GENES') {
      handleGeneFilterTypeChange(validation.detectedType);
    }
  };

  const handleSearchTypeChange = (newSearchType: string) => {
    setSearchType(newSearchType);

    if (newSearchType === 'VARIANTS' && text.trim()) {
      // Validation will be handled by the hook
      validateInput();
    }
  };

  const handleModalSubmit = async (params: any) => {
    setEnrichmentParams(params);
    setShowModal(false);

    const result = await performEnrichment(searchType, text, geneFilterType, params);
    if (result) {
      setCsvData(result.csvData);
      setMetadataContent(result.metadataData);
    }
  };

  const goBack = () => {
    if (enrichmentFrom?.from === 'advancedSearch' || enrichmentFrom?.from === 'advancedSearchSelectedItems') {
      const { searchType, selectedFilter, text } = enrichmentFrom;

      navigate('/advancedSearch', {
        state: { searchType, selectedFilter, text },
      });
    } else {
      navigate('/');
    }

    clearEnrichmentFrom();
  };

  const handleEnrichmentClick = async () => {
    if (validation && !validation.isValid) {
      alert('Please fix the validation errors before running enrichment');
      return;
    }

    const inputs = text
      .split('\n')
      .map((line: string) => line.trim())
      .filter((line: string) => line);

    await fetchSuggestions(inputs);
    setShowModal(true);
  };

  const handleDownloadResults = async () => {
    if (!csvData || !metadataContent) {
      alert('No data available to download');
      return;
    }

    try {
      const csvForDownload = (() => {
        if (!enrichmentResults || enrichmentResults.length === 0) return csvData;

        const escapeCsvTailValue = (value: unknown, delimiter: string): string => {
          if (value === null || value === undefined) return '';
          const str = String(value);
          if (new RegExp(`[${delimiter}"\\n\\r]`).test(str)) return `"${str.replace(/"/g, '""')}"`;
          return str;
        };

        const splitCsvRecordsPreservingQuotes = (text: string): string[] => {
          const records: string[] = [];
          let current = '';
          let inQuotes = false;

          for (let i = 0; i < text.length; i += 1) {
            const ch = text[i];

            if (ch === '"') {
              if (inQuotes && text[i + 1] === '"') {
                current += '""';
                i += 1;
              } else {
                inQuotes = !inQuotes;
                current += ch;
              }
              continue;
            }

            if (ch === '\n' && !inQuotes) {
              records.push(current.replace(/\r$/, ''));
              current = '';
              continue;
            }

            current += ch;
          }

          if (current.length > 0) records.push(current.replace(/\r$/, ''));
          return records;
        };

        const records = splitCsvRecordsPreservingQuotes(csvData);
        if (records.length < 2) return csvData;

        const headerLine = records[0];

        if (headerLine.includes('oddsRatio') && headerLine.includes('score')) return csvData;

        const splitCsvLine = (line: string, delimiterChar: string): string[] => {
          const fields: string[] = [];
          let current = '';
          let inQuotes = false;

          for (let i = 0; i < line.length; i += 1) {
            const ch = line[i];

            if (ch === '"') {
              if (inQuotes && line[i + 1] === '"') {
                current += '""';
                i += 1;
              } else {
                inQuotes = !inQuotes;
              }
              continue;
            }

            if (ch === delimiterChar && !inQuotes) {
              fields.push(current);
              current = '';
              continue;
            }

            current += ch;
          }

          fields.push(current);
          return fields;
        };

        const candidateDelimiters = [',', ';', '\t'];
        const firstDataLine = records.find((r, i) => i > 0 && r.trim().length > 0) ?? records[1];

        const best = candidateDelimiters
          .map((delim, index) => {
            const headerFields = splitCsvLine(headerLine, delim);
            const dataFields = splitCsvLine(firstDataLine, delim);
            const ok = headerFields.length === dataFields.length;
            return { delim, ok, count: headerFields.length, index };
          })
          .filter(x => x.ok)
          .sort((a, b) => {
            const byCount = b.count - a.count;
            return byCount !== 0 ? byCount : a.index - b.index;
          })[0];

        const delimiter = best?.delim ?? ',';

        const nextHeaderLine = `${headerLine}${delimiter}oddsRatio${delimiter}score`;

        const rows = records.slice(1).map((row, idx) => {
          const item = enrichmentResults[idx] as EnrichmentResult | undefined;
          const oddsRatio = item?.oddsRatio;
          const score = item?.score;
          const oddsRatioStr = escapeCsvTailValue(oddsRatio, delimiter);
          const scoreStr = escapeCsvTailValue(score, delimiter);
          return `${row}${delimiter}${oddsRatioStr}${delimiter}${scoreStr}`;
        });

        return [nextHeaderLine, ...rows].join('\n');
      })();

      await downloadZIP(csvForDownload, metadataContent, searchType);
    } catch (error) {
      console.error('Error downloading results:', error);
      alert('Error downloading results. Please try again.');
    }
  };

  const handleDownloadGraph = async () => {
    try {
      await downloadGraphPNG();
    } catch (error) {
      console.error('Error downloading graph:', error);
      alert('Error downloading graph. Please try again.');
    }
  };

  const canRunEnrichment = text.trim() && (!validation || validation.isValid);

  const getAvailableGeneFilterOptions = () => {
    const baseOptions = [
      { value: 'geneSymbol', label: 'Gene symbol' },
      { value: 'ensemblId', label: 'Ensembl ID' },
      { value: 'uniprotId', label: 'UniProt ID' },
      { value: 'geneId', label: 'NCBI Gene ID' },
    ];

    if (enrichmentFrom?.from === 'advancedSearchSelectedItems' && enrichmentFrom?.selectedFilter === 'name') {
      return [{ value: 'name', label: 'Gene name' }, ...baseOptions];
    }

    return baseOptions;
  };

  const availableGeneFilterOptions = getAvailableGeneFilterOptions();

  return (
    <div className="apply-trial-page">
      <BackgroundPages />
      <div className="relative z-10">
        <div className="container mx-auto md:px-4 py-8">
          {enrichmentLoading && <Loader style={{ position: 'fixed' }} />}

          <div className="mb-4 text-left">
            <SharedBadge
              text="Disease Enrichment Tool"
              icon={<img src={enrichmentIcon} alt="Enrichment" className="h-4 w-4 mr-1" />}
              variant="secondary"
              size="sm"
            />
            <h1 className="text-5xl font-semibold my-3 ">Disease Enrichment Analysis</h1>
            <p className="mb-4 text-lg font-medium text-blue w-3/4">
              Perform enrichment analysis on your gene or variant lists to identify significant associations
              with diseases and phenotypes from the largest curated collection available.
            </p>
          </div>

          <RestrictedAccess isRestricted={!isAuthenticated}>
            <div className="bg-white p-4 border border-gray-200 rounded-2xl">
              {/* Search Type Selection */}
              <div className="mb-6">
                <TabSelector
                  options={tabs.map(type => ({ label: type.toUpperCase(), value: type }))}
                  variant="primary"
                  size="sm"
                  onChange={handleSearchTypeChange}
                />
              </div>

              {/* Gene/Variant identifier dropdown */}
              {searchType === 'GENES' ? (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Identifier type</label>
                  <select
                    value={geneFilterType}
                    onChange={e => handleGeneFilterTypeChange(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                  >
                    {availableGeneFilterOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  {geneFilterType === 'name' ? (
                    <p className="text-xs mt-1">
                      Gene names from Advanced Search. Changing this filter will clear the current list.
                    </p>
                  ) : null}
                </div>
              ) : (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variant identifier</label>
                  <input
                    readOnly
                    value="rs number"
                    className="w-full p-2 border border-gray-300 bg-gray-50 rounded-2xl"
                    disabled
                  />
                </div>
              )}

              {/* Text Input Area */}
              <div className="mb-2">
                <label className="block text-sm font-medium text-gray-700 mb-2 ">
                  {geneFilterType === 'name' ? (
                    'Selected gene names from Advanced Search:'
                  ) : (
                    <p className="ml-2">
                      Add your search list <span className="font-thin text-xs">(separate items by line)</span>
                      :
                    </p>
                  )}
                </label>
                <textarea
                  placeholder={
                    geneFilterType === 'name'
                      ? 'Gene names from Advanced Search (read-only)'
                      : `Enter ${searchType.toLowerCase()} (one per line)`
                  }
                  value={text}
                  onChange={handleQueryChange}
                  readOnly={geneFilterType === 'name'}
                  className={`w-full p-3 border rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100 ${
                    geneFilterType === 'name'
                      ? 'bg-gray-50 cursor-not-allowed'
                      : validation && !validation.isValid
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300'
                  }`}
                  rows={8}
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-sm text-gray-500">Items: {getItemCount(text)}</p>
                </div>
              </div>

              {/* Validation Messages */}
              {showValidation && (
                <ValidationMessage validation={validation} onSuggestionClick={handleAutoDetectSuggestion} />
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 md:gap-0">
                <SharedButton
                  id="enrichment_back"
                  variant="secondary"
                  onClick={goBack}
                  className="filter-upload"
                  disabled={!isAuthenticated}
                >
                  Back
                </SharedButton>

                <SharedButton
                  id="enrichment_run"
                  variant="gradient"
                  onClick={handleEnrichmentClick}
                  disabled={
                    enrichmentLoading ||
                    !canRunEnrichment ||
                    (isTrial && getItemCount(text) > 10) ||
                    !isAuthenticated
                  }
                  className={`filter-upload ${
                    !canRunEnrichment || (isTrial && getItemCount(text) > 10)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  Run Enrichment
                  {enrichmentLoading && <Loader style={{ position: 'fixed' }} />}
                </SharedButton>

                <SharedButton
                  id="enrichment_clear"
                  variant="secondary"
                  onClick={clearInputText}
                  disabled={!isAuthenticated}
                  className={`filter-upload ${
                    !canRunEnrichment || (isTrial && getItemCount(text) > 10)
                      ? 'opacity-50 cursor-not-allowed'
                      : ''
                  }`}
                >
                  Clear List
                </SharedButton>

                <SharedButton
                  id="enrichment_examples"
                  variant="gradient"
                  onClick={showExamples}
                  className="filter-upload"
                  disabled={!isAuthenticated}
                >
                  Show Examples
                </SharedButton>
                {enrichmentResults && csvData && (
                  <SharedButton
                    id="enrichment_download"
                    variant="secondaryOutline"
                    onClick={handleDownloadResults}
                    className="filter-upload"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4 mr-1" /> Download Results (ZIP)
                  </SharedButton>
                )}
              </div>
            </div>
          </RestrictedAccess>

          {/* Enrichment Modal */}
          {showModal && (
            <EnrichmentModal
              isOpen={showModal}
              onClose={() => setShowModal(false)}
              onSubmit={handleModalSubmit}
              initialParams={enrichmentParams}
              searchType={searchType}
              notFoundItems={notFoundItems}
              user={user}
            />
          )}

          {/* Alerts */}
          {showErrorAlert && (
            <Alert
              message={`There was an error running the ${searchType.toLowerCase()} enrichment analysis.`}
              type="error"
            />
          )}

          {showWarningAlert && (
            <Alert
              message="There are no data available for this enrichment analysis with the provided identifiers."
              type="warning"
            />
          )}

          {showSuccessAlert && <Alert message="Enrichment analysis completed successfully." type="success" />}

          {showApiKeyAlert && (
            <Alert message="You need to set your API key." type="warning">
              <span
                onClick={() => navigate('/Profile-area#apiKey')}
                className="font-bold ml-2 cursor-pointer hover:text-blue-600"
              >
                Set API Key
              </span>
            </Alert>
          )}

          {/* Enrichment Results Visualization */}
          <div>
            {enrichmentResults && (
              <EnrichmentSwarmPlot ref={swarmRef} data={enrichmentResults} onDownload={handleDownloadGraph} />
            )}
          </div>

          {/* Trial Limit Modal */}
          <SharedModal
            open={showTrialLimitModal}
            setOpen={value => {
              setShowTrialLimitModal(value);
              if (!value) {
                setTrialLimitDismissed(true);
              }
            }}
            title="Trial User Limit"
            content={
              <div className="mt-4">
                <p className="text-gray-700 mb-4">
                  Trial users can only run enrichment analysis with up to <strong>10 items</strong>, but will
                  receive results for up to <strong>30 items</strong> from the analysis.
                </p>
                <p className="text-gray-700 mb-4">
                  You currently have <strong>{getItemCount(text)} items</strong> in your list.
                </p>
                <p className="text-gray-600 mb-6">
                  To analyze larger datasets (up to 4,000 items), please upgrade your plan.
                </p>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowTrialLimitModal(false);
                      setTrialLimitDismissed(true);
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      setShowTrialLimitModal(false);
                      setTrialLimitDismissed(true);
                      navigate('/plans');
                    }}
                    className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
                  >
                    View Plans
                  </button>
                </div>
              </div>
            }
          />
        </div>
        <section className="home-stats-wrapper mt-4">
          <HomeStats />
        </section>
      </div>
    </div>
  );
};

export default Enrichment;
